/**
 * Where the workspace page sits in its frame: the zoom and offset of the
 * page, kept pure so the rules are pinned by tests.
 *
 * The frame is the pane and never moves. The page is laid out once at its
 * zoom-1 size and scaled and translated inside it, as the report's document
 * view does, so zooming never pushes the page (or its caption) out of the
 * pane and never waits on a re-render to show.
 */

/** Room either side of the page, so a page that fits does not touch the pane's edges. */
export const SIDE_GUTTER = 24
/** Room above the page: a document opens at its first line, just under the caption. */
export const TOP_GUTTER = 8
/** Room below the page, so its bottom edge is visibly the end of the sheet. */
export const BOTTOM_GUTTER = 24

export interface SurfaceView {
  zoom: number
  /** The page's top-left corner in the frame, in CSS pixels. */
  tx: number
  ty: number
}

export interface SurfaceBounds {
  /** The frame, in CSS pixels. */
  frameW: number
  frameH: number
  /** The page at zoom 1, in CSS pixels. */
  pageW: number
  pageH: number
}

/** A rectangle on the page at zoom 1, in CSS pixels. */
export interface StageRect {
  x: number
  y: number
  w: number
  h: number
}

/**
 * The most pixels a page is ever rasterised to. Just under iOS Safari's canvas
 * limit (16,777,216 px), past which a canvas silently stays blank; it also
 * keeps a desktop's bitmap, held twice while a render swaps in, to ~64 MB.
 */
export const MAX_RASTER_PIXELS = 16_000_000

/**
 * The zoom to rasterise a page at: the zoom it is shown at, unless that would
 * draw more than `maxPixels`. Past the cap the bitmap is stretched on screen
 * instead, a little soft rather than blank. Never below 1.
 *
 * `pageWidth` is the page's laid-out width at zoom 1, in CSS pixels;
 * `pointWidth`/`pointHeight` its size in PDF points.
 */
export function cappedRasterZoom(
  zoom: number,
  pageWidth: number,
  pointWidth: number,
  pointHeight: number,
  maxPixels = MAX_RASTER_PIXELS,
): number {
  const wanted = Math.max(1, zoom)
  if (pageWidth <= 0 || pointWidth <= 0 || pointHeight <= 0) return wanted
  // Pixels drawn = (scale * pointWidth) * (scale * pointHeight), with
  // scale = pageWidth * rasterZoom / pointWidth.
  const ceiling = (Math.sqrt(maxPixels / (pointWidth * pointHeight)) * pointWidth) / pageWidth
  return Math.max(1, Math.min(wanted, ceiling))
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/** The view a page opens at: its first line, at the left margin. */
export function initialView(zoom: number): SurfaceView {
  return { zoom, tx: SIDE_GUTTER, ty: TOP_GUTTER }
}

/**
 * Keep the page where a reader can find it. Along an axis where it fits, it
 * holds a fixed place (centred across, at the top down); where it does not, it
 * may move only until an edge reaches its gutter, so no amount of panning
 * loses the page off the side of the pane.
 */
export function clampSurfaceView(view: SurfaceView, b: SurfaceBounds): SurfaceView {
  const scaledW = b.pageW * view.zoom
  const scaledH = b.pageH * view.zoom
  const tx =
    scaledW + 2 * SIDE_GUTTER <= b.frameW
      ? (b.frameW - scaledW) / 2
      : clamp(view.tx, b.frameW - scaledW - SIDE_GUTTER, SIDE_GUTTER)
  const ty =
    scaledH + TOP_GUTTER + BOTTOM_GUTTER <= b.frameH
      ? TOP_GUTTER
      : clamp(view.ty, b.frameH - scaledH - BOTTOM_GUTTER, TOP_GUTTER)
  return { zoom: view.zoom, tx, ty }
}

/** Whether the page, at this zoom, is larger than the frame in either direction. */
export function canPanSurface(zoom: number, b: SurfaceBounds): boolean {
  return (
    b.pageW * zoom + 2 * SIDE_GUTTER > b.frameW + 0.5 ||
    b.pageH * zoom + TOP_GUTTER + BOTTOM_GUTTER > b.frameH + 0.5
  )
}

/** Change the zoom, keeping the page point under (`cx`, `cy`) in the frame where it is. */
export function zoomSurfaceAround(
  view: SurfaceView,
  nextZoom: number,
  cx: number,
  cy: number,
  b: SurfaceBounds,
): SurfaceView {
  const ratio = nextZoom / view.zoom
  return clampSurfaceView(
    { zoom: nextZoom, tx: cx - (cx - view.tx) * ratio, ty: cy - (cy - view.ty) * ratio },
    b,
  )
}

/** Move the page by (`dx`, `dy`) from `from`. */
export function panSurface(
  from: SurfaceView,
  dx: number,
  dy: number,
  b: SurfaceBounds,
): SurfaceView {
  return clampSurfaceView({ zoom: from.zoom, tx: from.tx + dx, ty: from.ty + dy }, b)
}

/** The view at `zoom` that puts `rect` in the middle of the frame, as near as the page allows. */
export function centreSurfaceOn(rect: StageRect, zoom: number, b: SurfaceBounds): SurfaceView {
  return clampSurfaceView(
    {
      zoom,
      tx: b.frameW / 2 - (rect.x + rect.w / 2) * zoom,
      ty: b.frameH / 2 - (rect.y + rect.h / 2) * zoom,
    },
    b,
  )
}
